import React, { useState, useEffect } from 'react';
import { Plus, Loader2, PackagePlus } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';
import { useAuth } from '../context/AuthContext';
import ProductEditTable from './ProductEditTable';

/**
 * MultiProductImportDialog — Import multi-produits MANUEL unifié.
 * STRICTEMENT les mêmes champs que l'Import New (facture) :
 * code, article, type, rayon, DLC, conditionnement (qté/unité/type),
 * prix colis, détaillable (qté/unité), prix catalogue.
 * Une seule ligne suffit pour importer un produit seul.
 */

let rowSeq = 0;
const emptyRow = () => ({
  id: `row-${++rowSeq}`,
  code: '',
  article: '',
  product_type: '',
  rayon: '',
  dlc: false,
  supplier_packaging_quantity: '',
  supplier_packaging_unit: '',
  supplier_packaging_type: 'colis',
  supplier_package_price: '',
  is_divisible: false,
  divisible_quantity: '',
  divisible_unit: '',
  catalog_price: '',
});

export const MultiProductImportDialog = ({ open, onOpenChange, suppliers, onImportSuccess }) => {
  const { api } = useAuth();
  const [products, setProducts] = useState([emptyRow()]);
  const [supplierId, setSupplierId] = useState('');
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [rayonOptions, setRayonOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [packagingTypeOptions, setPackagingTypeOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkType, setBulkType] = useState('');
  const [bulkRayon, setBulkRayon] = useState('');

  useEffect(() => {
    if (open && !typeOptions.length) {
      Promise.all([api.get('/types'), api.get('/rayons'), api.get('/packaging-options')])
        .then(([t, r, p]) => {
          setTypeOptions(t.data || []);
          setRayonOptions(r.data || []);
          setUnitOptions(p.data?.units || []);
          setPackagingTypeOptions(p.data?.types || []);
        })
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      setProducts([emptyRow()]);
      setSupplierId('');
      setUpdateExisting(false);
      setSelectedIds(new Set());
      setBulkType('');
      setBulkRayon('');
    }
  }, [open]);

  const updateProduct = (id, field, value) => {
    setProducts(prev => prev.map(p => {
      if (p.id !== id) return p;
      const updated = { ...p, [field]: value };
      // Auto-calcul du prix catalogue (même convention que l'Import New)
      if (['supplier_packaging_quantity', 'supplier_package_price', 'is_divisible', 'divisible_quantity'].includes(field)) {
        const qty = parseFloat(updated.supplier_packaging_quantity) || 0;
        const price = parseFloat(updated.supplier_package_price) || 0;
        const divQty = parseFloat(updated.divisible_quantity) || 1;
        if (updated.is_divisible && qty > 0 && price > 0 && divQty > 0) {
          updated.catalog_price = (price / qty * divQty).toFixed(2);
        } else if (price > 0) {
          updated.catalog_price = price.toFixed(2);
        }
      }
      return updated;
    }));
  };

  const removeProduct = (id) => {
    setProducts(prev => prev.length > 1 ? prev.filter(p => p.id !== id) : prev);
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const addRow = () => setProducts(prev => [...prev, emptyRow()]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? new Set(products.map(p => p.id)) : new Set());
  };

  const applyBulk = () => {
    if (selectedIds.size === 0) {
      toast.error('Sélectionnez au moins un produit (cases à cocher)');
      return;
    }
    if (!bulkType && !bulkRayon) {
      toast.error('Choisissez un type et/ou un rayon à appliquer');
      return;
    }
    setProducts(prev => prev.map(p => {
      if (!selectedIds.has(p.id)) return p;
      return {
        ...p,
        ...(bulkType ? { product_type: bulkType } : {}),
        ...(bulkRayon ? { rayon: bulkRayon } : {}),
      };
    }));
    toast.success(`${selectedIds.size} produit(s) mis à jour`);
  };

  const validProducts = products.filter(p => p.code && p.article);

  const handleImport = async () => {
    if (!supplierId) {
      toast.error('Veuillez sélectionner un fournisseur');
      return;
    }
    if (!validProducts.length) {
      toast.error('Aucun produit valide (code + article requis)');
      return;
    }
    setImporting(true);
    try {
      const response = await api.post('/products/import', {
        supplier_id: supplierId,
        products: validProducts.map(p => ({
          code: p.code,
          article: p.article,
          product_type: p.product_type || '',
          rayon: p.rayon || '',
          dlc: Boolean(p.dlc),
          supplier_packaging_quantity: p.supplier_packaging_quantity !== '' && p.supplier_packaging_quantity != null ? parseFloat(p.supplier_packaging_quantity) : null,
          supplier_packaging_unit: p.supplier_packaging_unit || null,
          supplier_packaging_type: p.supplier_packaging_type || null,
          supplier_package_price: p.supplier_package_price !== '' && p.supplier_package_price != null ? parseFloat(p.supplier_package_price) : null,
          is_divisible: Boolean(p.is_divisible),
          divisible_quantity: p.divisible_quantity !== '' && p.divisible_quantity != null ? parseFloat(p.divisible_quantity) : null,
          divisible_unit: p.divisible_unit || null,
          catalog_price: p.catalog_price !== '' && p.catalog_price != null ? parseFloat(p.catalog_price) : null,
          catalog_unit: p.is_divisible ? (p.divisible_unit || null) : null,
          unit_price: p.catalog_price !== '' && p.catalog_price != null ? parseFloat(p.catalog_price) : null,
        })),
        update_existing: updateExisting,
      });
      const { created, updated, skipped, errors } = response.data;
      toast.success(`✅ ${created} créé(s), ${updated} mis à jour, ${skipped} ignoré(s)`);
      if (errors?.length) {
        errors.slice(0, 3).forEach(e => toast.warning(e));
      }
      onImportSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(formatErrorMessage(error, "Erreur lors de l'import"));
    } finally {
      setImporting(false);
    }
  };

  const handleOpenChange = (value) => {
    if (!value && validProducts.length > 0 && !importing) {
      if (!window.confirm('Fermer sans importer ? Les produits saisis seront perdus.')) return;
    }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[95vw] w-[1400px] max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="multi-product-import-dialog"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackagePlus className="w-5 h-5 text-emerald-600" />
            Import multi-produits
          </DialogTitle>
          <DialogDescription>
            Saisie manuelle — mêmes champs que l'import par facture. Une seule ligne suffit pour un produit seul.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Fournisseur — obligatoire */}
          <div className="flex flex-wrap items-end gap-4 p-3 border rounded-lg bg-white">
            <div className="flex-1 min-w-[260px]">
              <Label className="text-sm font-semibold">
                Fournisseur * <span className="font-normal text-gray-500">(tous les produits saisis lui seront rattachés)</span>
              </Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger className="mt-1" data-testid="multi-supplier-select">
                  <SelectValue placeholder="Sélectionner le fournisseur..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers?.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch
                checked={updateExisting}
                onCheckedChange={setUpdateExisting}
                data-testid="multi-update-existing-switch"
              />
              <Label className="text-sm">Mettre à jour les produits existants (même code)</Label>
            </div>
          </div>

          {/* Actions groupées : type / rayon sur la sélection */}
          <div className="flex flex-wrap items-end gap-3 p-3 border rounded-lg bg-emerald-50/50 border-emerald-200" data-testid="multi-bulk-actions-bar">
            <div className="text-sm font-semibold text-gray-800 pb-2">
              Actions groupées
              <span className="font-normal text-gray-500"> — {selectedIds.size} sélectionné(s)</span>
            </div>
            <div className="min-w-[170px]">
              <Label className="text-xs text-gray-600">Type de produit</Label>
              <Select value={bulkType || undefined} onValueChange={setBulkType}>
                <SelectTrigger className="h-9 bg-white" data-testid="multi-bulk-type-select">
                  <SelectValue placeholder="Type..." />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[190px]">
              <Label className="text-xs text-gray-600">Rayon</Label>
              <Select value={bulkRayon || undefined} onValueChange={setBulkRayon}>
                <SelectTrigger className="h-9 bg-white" data-testid="multi-bulk-rayon-select">
                  <SelectValue placeholder="Rayon..." />
                </SelectTrigger>
                <SelectContent>
                  {rayonOptions.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              className="h-9 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              onClick={applyBulk}
              disabled={selectedIds.size === 0 || (!bulkType && !bulkRayon)}
              data-testid="multi-apply-bulk-button"
            >
              Appliquer à la sélection
            </Button>
          </div>

          {/* Tableau de saisie — formulaire unifié */}
          <ProductEditTable
            products={products}
            onProductUpdate={updateProduct}
            onProductRemove={removeProduct}
            minProducts={1}
            showDlc
            typeOptions={typeOptions}
            rayonOptions={rayonOptions}
            unitOptions={unitOptions}
            packagingTypeOptions={packagingTypeOptions}
            selectable
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
          />

          <Button
            type="button"
            variant="outline"
            onClick={addRow}
            className="border-dashed"
            data-testid="multi-add-row-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Ajouter une ligne
          </Button>
        </div>

        <div className="border-t pt-3 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {validProducts.length} produit(s) prêt(s) — code + article requis par ligne
          </span>
          <Button
            className="bg-[#1B4332] hover:bg-[#0D2E1F]"
            onClick={handleImport}
            disabled={importing || !validProducts.length}
            data-testid="multi-confirm-import-button"
          >
            {importing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Import...</>
            ) : (
              <>Importer {validProducts.length} produit(s)</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MultiProductImportDialog;
